import { useMemo } from "react";
import type { DashboardTab, TabGroup } from "@app/config/tabs";
import { tabGroups as allTabGroups } from "@app/config/tabs";
import { useUiStore, type UiState } from "@app/state/uiStore";
import { filterGroupsByVisibleTabs, getTabsForGroup } from "@app/utils/tabFiltering";

type TabNavigationProps = {
  tabs: DashboardTab[];
  activeTab: string;
  grouped: boolean;
};

const GROUP_ICONS: Record<string, JSX.Element> = {
  home: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  ),
  chart: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  ),
  cpu: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="4" width="16" height="16" rx="2" ry="2" />
      <rect x="9" y="9" width="6" height="6" />
      <line x1="9" y1="1" x2="9" y2="4" />
      <line x1="15" y1="1" x2="15" y2="4" />
      <line x1="9" y1="20" x2="9" y2="23" />
      <line x1="15" y1="20" x2="15" y2="23" />
      <line x1="20" y1="9" x2="23" y2="9" />
      <line x1="20" y1="14" x2="23" y2="14" />
      <line x1="1" y1="9" x2="4" y2="9" />
      <line x1="1" y1="14" x2="4" y2="14" />
    </svg>
  ),
  link: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  ),
  users: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  settings: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  ),
  target: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" />
    </svg>
  ),
};

type FlatTabIcons = Record<string, JSX.Element>;

const FLAT_TAB_ICONS: FlatTabIcons = {
  home: GROUP_ICONS.home,
  chart: GROUP_ICONS.chart,
  link: GROUP_ICONS.link,
  settings: GROUP_ICONS.settings,
};

function GroupedNavigation({ tabs, activeTab }: Omit<TabNavigationProps, "grouped">) {
  const setActiveTab = useUiStore((state: UiState) => state.setActiveTab);
  const activeGroup = useUiStore((state: UiState) => state.activeGroup);

  const visibleGroups = useMemo(
    () => filterGroupsByVisibleTabs(allTabGroups, tabs),
    [tabs],
  );

  const subTabs = useMemo(
    () => getTabsForGroup(activeGroup, tabs),
    [tabs, activeGroup],
  );

  const isSingleTabGroup = subTabs.length <= 1;

  const handleGroupClick = (group: TabGroup) => {
    if (group.id === activeGroup) {
      return;
    }
    const targetSubTabs = getTabsForGroup(group.id, tabs);
    const firstTabId = targetSubTabs.length > 0 ? targetSubTabs[0].id : group.id;
    setActiveTab(firstTabId);
  };

  return (
    <div className="tab-navigation-wrapper">
      <div className="tab-group-bar">
        {visibleGroups.map((group) => {
          const isActive = group.id === activeGroup;
          return (
            <button
              key={group.id}
              type="button"
              className={`tab-group-btn${isActive ? " active" : ""}`}
              onClick={() => handleGroupClick(group)}
              title={group.title}
            >
              <span className="tab-group-icon">
                {GROUP_ICONS[group.icon]}
              </span>
              <span className="tab-group-label">{group.title}</span>
              {isActive && <span className="tab-group-glow" />}
            </button>
          );
        })}
      </div>

      {!isSingleTabGroup && subTabs.length > 1 && (
        <div className="tab-sub-bar">
          <div className="tab-sub-bar-inner">
            {subTabs.map((tab) => {
              const isActive = tab.id === activeTab;
              return (
                <button
                  key={tab.id}
                  type="button"
                  className={`tab-sub-btn${isActive ? " active" : ""}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.title}
                  {isActive && <span className="tab-sub-indicator" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function FlatNavigation({ tabs, activeTab }: Omit<TabNavigationProps, "grouped">) {
  const setActiveTab = useUiStore((state: UiState) => state.setActiveTab);

  return (
    <div className="tab-navigation-wrapper tab-navigation-flat">
      <div className="tab-flat-bar">
        {tabs.map((tab) => {
          const isActive = tab.id === activeTab;
          const groupConfig = allTabGroups.find((g) => g.id === tab.group);
          const iconKey = groupConfig?.icon ?? "settings";
          return (
            <button
              key={tab.id}
              type="button"
              className={`tab-flat-btn${isActive ? " active" : ""}`}
              onClick={() => setActiveTab(tab.id)}
              title={tab.description}
            >
              <span className="tab-flat-icon">
                {FLAT_TAB_ICONS[iconKey] ?? FLAT_TAB_ICONS.settings}
              </span>
              <span className="tab-flat-label">{tab.title}</span>
              {isActive && <span className="tab-flat-glow" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function TabNavigation({ tabs, activeTab, grouped }: TabNavigationProps) {
  if (!grouped) {
    return <FlatNavigation tabs={tabs} activeTab={activeTab} />;
  }
  return <GroupedNavigation tabs={tabs} activeTab={activeTab} />;
}

export default TabNavigation;
